import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useState, type MouseEvent } from 'react';

export type ChartMetric = 'rate' | 'packages';

export type DayPoint = {
  day: string;
  value: number;
  packagesLabel: string;
  rateLabel: string;
};

const VIEW_W = 720;
const VIEW_H = 220;
const PAD_L = 48;
const PAD_R = 8;
const PAD_T = 12;
const PAD_B = 28;

function formatAxisDay(day: string): string {
  const parts = day.split('-');
  if (parts.length !== 3) {
    return day;
  }
  return `${Number(parts[1])}/${Number(parts[2])}`;
}

function formatDayLong(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date) {
    return day;
  }
  return new Date(Date.UTC(year, month - 1, date)).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function niceMax(value: number): number {
  if (value <= 0) {
    return 1;
  }
  const exponent = Math.pow(10, Math.floor(Math.log10(value)));
  const fraction = value / exponent;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  return nice * exponent;
}

function formatTick(value: number): string {
  if (value >= 100) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

export function DailyBarChart({
  title,
  points,
  metric,
  onMetricChange,
}: {
  title: string;
  points: DayPoint[];
  metric: ChartMetric;
  onMetricChange: (metric: ChartMetric) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const max = niceMax(Math.max(...points.map(point => point.value), 0));
  const plotW = VIEW_W - PAD_L - PAD_R;
  const plotH = VIEW_H - PAD_T - PAD_B;
  const slot = points.length > 0 ? plotW / points.length : plotW;
  const barW = Math.max(2, slot * 0.62);
  const labelEvery = points.length > 20 ? 5 : points.length > 10 ? 2 : 1;
  const hovered = hover !== null ? points[hover] : null;

  const onMove = (event: MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * VIEW_W;
    const index = Math.floor((x - PAD_L) / slot);
    setHover(index >= 0 && index < points.length ? index : null);
  };

  return (
    <Box>
      <Flex justify='space-between' align='center' gap={3} mb={1} wrap='wrap'>
        <Text fontWeight='medium'>{title}</Text>
        <Flex gap={1}>
          <Button
            size='xs'
            colorPalette='primary'
            variant={metric === 'rate' ? 'solid' : 'outline'}
            aria-pressed={metric === 'rate'}
            onClick={() => onMetricChange('rate')}
          >
            Rate
          </Button>
          <Button
            size='xs'
            colorPalette='primary'
            variant={metric === 'packages' ? 'solid' : 'outline'}
            aria-pressed={metric === 'packages'}
            onClick={() => onMetricChange('packages')}
          >
            Packages
          </Button>
        </Flex>
      </Flex>
      <Text fontSize='xs' color='gray.500' mb={2}>
        Hover a day for that day's packages and rate.
      </Text>
      <Box position='relative'>
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          width='100%'
          role='img'
          aria-label={title}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          {[0, 0.5, 1].map(fraction => {
            const y = PAD_T + plotH - fraction * plotH;
            const tick = max * fraction;
            return (
              <g key={fraction}>
                <line x1={PAD_L} x2={VIEW_W - PAD_R} y1={y} y2={y} stroke='#e2e8f0' strokeWidth={1} />
                <text x={PAD_L - 6} y={y + 4} textAnchor='end' fontSize={11} fill='#718096'>
                  {formatTick(tick)}
                </text>
              </g>
            );
          })}
          {points.map((point, index) => {
            const height = point.value <= 0 ? 0 : (point.value / max) * plotH;
            const x = PAD_L + index * slot + (slot - barW) / 2;
            const y = PAD_T + plotH - height;
            const active = hover === index;
            return (
              <g key={point.day}>
                <rect x={PAD_L + index * slot} y={PAD_T} width={slot} height={plotH} fill='transparent' />
                <rect
                  x={x}
                  y={height === 0 ? PAD_T + plotH - 1 : y}
                  width={barW}
                  height={height === 0 ? 1 : height}
                  rx={2}
                  fill={active ? '#075f50' : height === 0 ? '#cbd5e0' : '#0C8A6F'}
                />
                {index % labelEvery === 0 || (index === points.length - 1 && index % labelEvery > 2) ? (
                  <text
                    x={PAD_L + index * slot + slot / 2}
                    y={VIEW_H - 8}
                    textAnchor='middle'
                    fontSize={11}
                    fill='#718096'
                  >
                    {formatAxisDay(point.day)}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
        {hovered && hover !== null ? (
          <Box
            position='absolute'
            top={2}
            left={`${((PAD_L + hover * slot + slot / 2) / VIEW_W) * 100}%`}
            transform='translateX(-50%)'
            bg='gray.800'
            color='white'
            fontSize='xs'
            px={2}
            py={1}
            borderRadius='md'
            whiteSpace='nowrap'
            pointerEvents='none'
          >
            <Text fontSize='xs' color='white' fontWeight='medium'>
              {formatDayLong(hovered.day)}
            </Text>
            <Text fontSize='xs' color='white'>
              {hovered.packagesLabel}
            </Text>
            <Text fontSize='xs' color='white'>
              {hovered.rateLabel}
            </Text>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
